import React, { useState } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2, AlertTriangle } from 'lucide-react';
import {
    stcResourceService,
    StcResourceDto,
    BulkUpdateStcResourceRequest
} from '../../../../../services/stcResourceService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ResourceBulkUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedResourceIds: number[];
    data: StcResourceDto[];
    onUpdateSuccess: () => void;
    onDeleteSuccess: () => void;
}

interface UpdateFields {
    resourceNo: boolean;
    description1: boolean;
}

export const ResourceBulkUpdateModal: React.FC<ResourceBulkUpdateModalProps> = ({
    isOpen,
    onClose,
    selectedResourceIds,
    data,
    onUpdateSuccess,
    onDeleteSuccess
}) => {
    const { t } = useTranslation();
    // Tab state
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    // Get selected resources
    const selectedResources = data.filter(r => selectedResourceIds.includes(r.id));
    const selectedCount = selectedResources.length;
    const isSingleSelection = selectedCount === 1;
    const singleResource = isSingleSelection ? selectedResources[0] : null;

    // Edit state - pre-fill when single selection
    const [updateFields, setUpdateFields] = useState<UpdateFields>({
        resourceNo: isSingleSelection,
        description1: isSingleSelection
    });
    const [resourceNo, setResourceNo] = useState<number>(singleResource?.resourceNo ?? 0);
    const [description1, setDescription1] = useState(singleResource?.description1 ?? '');

    // Operation state
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

    // Count enabled fields
    const enabledFieldCount = Object.values(updateFields).filter(Boolean).length;

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            if (enabledFieldCount === 0) {
                setError(t('manager.modules.adminResources.resourceBulk.errorSelectField', {}, 'Velg minst ett felt å oppdatere'));
                return;
            }

            if (updateFields.resourceNo && selectedCount > 1) {
                setError(t('manager.modules.adminResources.resourceBulk.errorResourceNoSingleOnly', {}, 'Ressursnummer kan kun endres for én ressurs om gangen'));
                return;
            }

            if (updateFields.resourceNo && resourceNo <= 0) {
                setError(t('manager.modules.adminResources.resourceBulk.errorResourceNoPositive', {}, 'Ressursnummer må være større enn 0'));
                return;
            }

            const bulkUpdateRequests: BulkUpdateStcResourceRequest[] = selectedResources.map(item => {
                const request: BulkUpdateStcResourceRequest = {
                    companyNo: item.companyNo,
                    resourceNo: item.resourceNo,
                    description1: item.description1
                };

                if (updateFields.resourceNo) {
                    request.resourceNo = resourceNo;
                }

                if (updateFields.description1) {
                    request.description1 = description1;
                }

                return request;
            });

            await stcResourceService.bulkUpdate(bulkUpdateRequests);

            setShowSuccess(true);
            setShowUpdateConfirm(false);
            setTimeout(() => {
                setShowSuccess(false);
                onUpdateSuccess();
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminResources.resourceBulk.errorUpdate', {}, 'Feil ved oppdatering'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const idsToDelete = selectedResources.map(item => item.id);
            await stcResourceService.bulkDelete(idsToDelete);

            setShowSuccess(true);
            setShowDeleteConfirm(false);
            setTimeout(() => {
                setShowSuccess(false);
                onDeleteSuccess();
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminResources.resourceBulk.errorDelete', {}, 'Feil ved sletting'));
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            {/* Modal */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminResources.resourceBulk.title', {}, 'Vedlikehold Ressurser')}</h2>
                            <p className="text-sm text-blue-100">
                                {selectedCount} {selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser')} {t('manager.modules.adminResources.resourceBulk.selectedSuffix', {}, 'valgt')}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('edit')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'edit'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            {t('common.buttons.edit', {}, 'Rediger')}
                        </button>
                        <button
                            onClick={() => setActiveTab('delete')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'delete'
                                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            {t('common.buttons.delete', {}, 'Slett')}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">
                                        {activeTab === 'edit' ? t('manager.modules.adminResources.resourceBulk.updateComplete', {}, 'Oppdatering fullført!') : t('manager.modules.adminResources.resourceBulk.deleteComplete', {}, 'Sletting fullført!')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'edit' ? (
                            <>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">{t('manager.modules.adminResources.resourceBulk.bulkUpdateHeading', {}, 'Masseoppdatering')}</p>
                                            <p>{t('manager.modules.adminResources.resourceBulk.bulkUpdateInfo', {}, 'Velg feltene du ønsker å oppdatere. Kun avkryssede felt vil bli endret for alle valgte ressurser.')}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* ResourceNo field */}
                                <div className={`border rounded-lg p-4 transition-colors ${
                                    updateFields.resourceNo ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                                }`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <input
                                            type="checkbox"
                                            id="field-resourceNo"
                                            checked={updateFields.resourceNo}
                                            onChange={(e) => setUpdateFields(prev => ({
                                                ...prev,
                                                resourceNo: e.target.checked
                                            }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="field-resourceNo" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            {t('manager.modules.adminResources.resourceBulk.resourceNoLabel', {}, 'Ressurs nr.')}
                                        </label>
                                    </div>
                                    {updateFields.resourceNo && (
                                        <div className="mt-2 space-y-2">
                                            {selectedCount > 1 && (
                                                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                                                    <p className="text-xs text-amber-700">
                                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                                        {t('manager.modules.adminResources.resourceBulk.resourceNoSingleWarning', {}, 'Ressursnummer kan kun endres for én ressurs om gangen.')}
                                                    </p>
                                                </div>
                                            )}
                                            <input
                                                type="number"
                                                value={resourceNo || ''}
                                                onChange={(e) => setResourceNo(parseInt(e.target.value) || 0)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder={t('manager.modules.adminResources.resourceBulk.resourceNoPlaceholder', {}, 'Nytt ressursnummer')}
                                                min={1}
                                                disabled={selectedCount > 1}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Description1 field */}
                                <div className={`border rounded-lg p-4 transition-colors ${
                                    updateFields.description1 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                                }`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <input
                                            type="checkbox"
                                            id="field-description1"
                                            checked={updateFields.description1}
                                            onChange={(e) => setUpdateFields(prev => ({
                                                ...prev,
                                                description1: e.target.checked
                                            }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="field-description1" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            {t('common.fields.description', {}, 'Beskrivelse')}
                                        </label>
                                    </div>
                                    {updateFields.description1 && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                value={description1}
                                                onChange={(e) => setDescription1(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder={t('manager.modules.adminResources.resourceBulk.descriptionPlaceholder', {}, 'Ny beskrivelse for alle valgte ressurser')}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Summary */}
                                {enabledFieldCount > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <div className="text-sm text-green-800">
                                                <p className="font-medium">{t('manager.modules.adminResources.resourceBulk.readyToSave', {}, 'Klar til å lagre')}</p>
                                                <p>
                                                    {t('manager.modules.adminResources.resourceBulk.fieldsWillUpdate', { '0': enabledFieldCount, '1': selectedCount, '2': selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser') }, `${enabledFieldCount} felt vil bli oppdatert for ${selectedCount} ${selectedCount === 1 ? 'ressurs' : 'ressurser'}`)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Update Confirmation */}
                                {showUpdateConfirm && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-amber-800">{t('manager.modules.adminResources.resourceBulk.confirmUpdateTitle', {}, 'Bekreft oppdatering')}</p>
                                                <p className="text-sm text-amber-700 mt-1">
                                                    {t('manager.modules.adminResources.resourceBulk.confirmUpdateQuestion', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser') }, `Er du sikker på at du vil oppdatere ${selectedCount} ${selectedCount === 1 ? 'ressurs' : 'ressurser'}?`)}
                                                </p>
                                                <div className="mt-2 text-xs text-amber-700">
                                                    <p className="font-medium">{t('manager.modules.adminResources.resourceBulk.affectedResources', {}, 'Berørte ressurser:')}</p>
                                                    <div className="max-h-20 overflow-y-auto mt-1 space-y-0.5">
                                                        {selectedResources.slice(0, 20).map(r => (
                                                            <div key={r.id}>{t('manager.modules.adminResources.resourceBulk.resourceLineLabel', {}, 'Ressurs')} {r.resourceNo}: {r.description1}</div>
                                                        ))}
                                                        {selectedResources.length > 20 && (
                                                            <div className="italic">{t('manager.modules.adminResources.resourceBulk.andMore', { '0': selectedResources.length - 20 }, `...og ${selectedResources.length - 20} til`)}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 mt-3">
                                                    <button
                                                        onClick={handleBulkUpdate}
                                                        disabled={isSaving}
                                                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        {isSaving ? t('common.status.saving', {}, 'Lagrer...') : t('manager.modules.adminResources.resourceBulk.yesUpdate', {}, 'Ja, oppdater')}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowUpdateConfirm(false)}
                                                        className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                                    >
                                                        {t('common.buttons.cancel', {}, 'Avbryt')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-red-800">
                                            <p className="font-medium mb-1">{t('manager.modules.adminResources.resourceBulk.bulkDeleteHeading', {}, 'Massesletting')}</p>
                                            <p>{t('manager.modules.adminResources.resourceBulk.bulkDeleteInfo', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser') }, `Du er i ferd med å slette ${selectedCount} ${selectedCount === 1 ? 'ressurs' : 'ressurser'}. Denne handlingen kan ikke angres.`)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Selected items list */}
                                <div className="space-y-2">
                                    <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminResources.resourceBulk.resourcesToDelete', {}, 'Ressurser som vil bli slettet:')}</h3>
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                        {selectedResources.map(item => (
                                            <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                                                <span className="font-medium">{t('manager.modules.adminResources.resourceBulk.resourceLineLabel', {}, 'Ressurs')} {item.resourceNo}</span>: {item.description1}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Delete Confirmation */}
                                {showDeleteConfirm && (
                                    <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-red-800">{t('manager.modules.adminResources.resourceBulk.areYouSure', {}, 'Er du helt sikker?')}</p>
                                                <p className="text-sm text-red-700 mt-1">
                                                    {t('manager.modules.adminResources.resourceBulk.permanentDeleteWarning', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser') }, `Denne handlingen kan ikke angres. ${selectedCount} ${selectedCount === 1 ? 'ressurs' : 'ressurser'} vil bli permanent slettet.`)}
                                                </p>
                                                <div className="flex gap-2 mt-3">
                                                    <button
                                                        onClick={handleBulkDelete}
                                                        disabled={isDeleting}
                                                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                                    >
                                                        {isDeleting ? t('manager.modules.adminResources.resourceBulk.deleting', {}, 'Sletter...') : t('manager.modules.adminResources.resourceBulk.yesDeletePermanent', {}, 'Ja, slett permanent')}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowDeleteConfirm(false)}
                                                        className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                                    >
                                                        {t('common.buttons.cancel', {}, 'Avbryt')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
                        <div className="flex items-center justify-between gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.close', {}, 'Lukk')}
                            </button>

                            {activeTab === 'edit' ? (
                                <button
                                    onClick={() => setShowUpdateConfirm(true)}
                                    disabled={enabledFieldCount === 0 || isSaving || showUpdateConfirm}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        enabledFieldCount === 0 || isSaving || showUpdateConfirm
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                >
                                    <Save className="w-4 h-4" />
                                    {t('manager.modules.adminResources.resourceBulk.updateButton', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser') }, `Oppdater ${selectedCount} ${selectedCount === 1 ? 'ressurs' : 'ressurser'}`)}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isDeleting || showDeleteConfirm}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        isDeleting || showDeleteConfirm
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-red-600 text-white hover:bg-red-700'
                                    }`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {t('manager.modules.adminResources.resourceBulk.deleteButton', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.resourceBulk.resourceSingular', {}, 'ressurs') : t('manager.modules.adminResources.resourceBulk.resourcePlural', {}, 'ressurser') }, `Slett ${selectedCount} ${selectedCount === 1 ? 'ressurs' : 'ressurser'}`)}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
